import { format } from 'date-fns';
import { da, enGB } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { UI_MUTED } from '@/lib/typography';
import type { JournalWritingDraftStatus } from '@/hooks/useJournalWritingDraft';

interface JournalAutosaveStatusProps {
  status: JournalWritingDraftStatus;
  lastSavedAt: number | null;
  className?: string;
}

export function JournalAutosaveStatus({
  status,
  lastSavedAt,
  className,
}: JournalAutosaveStatusProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'da' ? da : enGB;
  let label = '';
  switch (status) {
    case 'saving':
      label = t('journal.autosave.saving');
      break;
    case 'saved':
      label = lastSavedAt
        ? t('journal.autosave.saved_at', { time: format(lastSavedAt, 'p', { locale }) })
        : t('journal.autosave.saved');
      break;
    case 'error':
      label = t('journal.autosave.error');
      break;
    case 'idle':
      label = lastSavedAt
        ? t('journal.autosave.saved_at', { time: format(lastSavedAt, 'p', { locale }) })
        : '';
      break;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
  if (!label) return null;
  return (
    <p
      className={cn(UI_MUTED, 'text-xs', status === 'error' && 'text-ds-amber-ink', className)}
      role="status"
    >
      {label}
    </p>
  );
}
